import type { TPlayerBootTelemetryHeartbeatBody, TPlayerBootTelemetrySnapshot } from '@lucimark/shared/models/player-boot-telemetry'
import {
  buildPlayerBootTelemetryHeartbeatBody,
  PLAYER_BOOT_SESSION_MARKER_KEY,
  PLAYER_CLEAN_SHUTDOWN_STORAGE_KEY

} from '@lucimark/shared/models/player-boot-telemetry'
import { fetchAndroidBootPowerHints } from '@/helpers/androidBootPowerHints'

let bootSnapshot: TPlayerBootTelemetrySnapshot | null = null
let bootTelemetrySent = false

function readStorage(key: string): string | null {
  if (typeof localStorage === 'undefined') {
    return null
  }
  try {
    return localStorage.getItem(key)
  }
  catch {
    return null
  }
}

function writeStorage(key: string, value: string): void {
  if (typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.setItem(key, value)
  }
  catch {
    // private mode / quota
  }
}

function removeStorage(key: string): void {
  if (typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.removeItem(key)
  }
  catch {
    // ignore
  }
}

/**
 * Reads the previous-session clean-shutdown marker and prepares boot telemetry for the first heartbeat(s).
 * Web/Android Tauri: uses localStorage. First install leaves `lastBootWasCleanShutdown` null.
 * Android: merges native power/battery boot hints when available (issue #493).
 */
export async function initPlayerBootTelemetry(): Promise<TPlayerBootTelemetrySnapshot> {
  const lastBootAt = new Date()
  let lastBootWasCleanShutdown: boolean | null = null
  const cleanFlag = readStorage(PLAYER_CLEAN_SHUTDOWN_STORAGE_KEY)
  const seenBefore = readStorage(PLAYER_BOOT_SESSION_MARKER_KEY) != null
  if (cleanFlag === 'true') {
    lastBootWasCleanShutdown = true
  }
  else if (seenBefore) {
    lastBootWasCleanShutdown = false
  }
  removeStorage(PLAYER_CLEAN_SHUTDOWN_STORAGE_KEY)
  writeStorage(PLAYER_BOOT_SESSION_MARKER_KEY, '1')

  let lastBootHadBatteryOrPowerEvent: boolean | null = null
  let lastBootKind: 'cold_boot' | 'app_restart' | null = null
  const androidHints = await fetchAndroidBootPowerHints()
  if (androidHints?.hadBatteryOrPowerEvent === true) {
    lastBootHadBatteryOrPowerEvent = true
  }
  if (androidHints?.bootKind === 'cold_boot' || androidHints?.bootKind === 'app_restart') {
    lastBootKind = androidHints.bootKind
  }

  bootSnapshot = {
    lastBootAt,
    lastBootWasCleanShutdown,
    lastBootHadBatteryOrPowerEvent,
    lastBootKind
  }
  bootTelemetrySent = false
  return bootSnapshot
}

export function getPlayerBootTelemetrySnapshot(): TPlayerBootTelemetrySnapshot | null {
  return bootSnapshot
}

/** Mark graceful shutdown so the next boot can infer a clean exit. */
export function markPlayerCleanShutdown(): void {
  writeStorage(PLAYER_CLEAN_SHUTDOWN_STORAGE_KEY, 'true')
}

/** Boot fields for the next heartbeat; omitted after the first successful attach per process boot. */
export function takePlayerBootTelemetryHeartbeatBody(): TPlayerBootTelemetryHeartbeatBody {
  if (bootTelemetrySent || bootSnapshot == null) {
    return {}
  }
  return buildPlayerBootTelemetryHeartbeatBody(bootSnapshot)
}

export function markPlayerBootTelemetrySent(): void {
  bootTelemetrySent = true
}

/** Test helper */
export function resetPlayerBootTelemetryForTests(): void {
  bootSnapshot = null
  bootTelemetrySent = false
}
